import { SnackbarProvider } from "notistack";
import { useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider, LocationState, useAuthContext } from "./Auth";
import { HouseProvider } from "./HouseContext";
import { SettingsProvider, useSettingsContext } from "./SettingsContext";
import Footer from "./components/Layout/Footer";
import Header from "./components/Layout/Header";
import { DialogCookieBanner } from "./dialogs/DialogCookieBanner";
import PageAllocationChecker from "./pages/PageAllocationChecker";
import { PageEditBookings } from "./pages/PageEditBookings";
import { PageEditUser } from "./pages/PageEditUser";
import { PageImpressum } from "./pages/PageImpressum";
import PageLogin from "./pages/PageLogin";
import PageMyBookings from "./pages/PageMyBookings";
import { PagePasswordReset } from "./pages/PagePasswordReset";
import { PageRegister } from "./pages/PageRegister";
import PageSelectLocation from "./pages/PageSelectLocation";
import PageSetUser from "./pages/PageSetUser";
import PageSettings from "./pages/PageSettings";
import PageUserProfile from "./pages/PageUserProfile";
import { PageBookingStepper } from "./pages/stepper/PageBookingStepper";
import { getCookie, setCookie } from "./utils/cookieutils";
import PageOccupancy from "./pages/PageOccupancy";

interface IRequireAuth
{
  onlyAdmin?: boolean;
}

const RequireAuth: React.FC<IRequireAuth> = (props) =>{
  const auth = useAuthContext()
  if (!auth.isLoggedIn()) {
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the home page.
    return <Navigate to="/login" state={{ from: window.location.pathname } as LocationState} replace />;
  }
  if (props.onlyAdmin && !auth.isAdmin()) {
    return <p>No permission to view this page</p>
  }

  return <>{props.children}</>;
}

const ConditionalAuth: React.FC<IRequireAuth> = (props) =>{
  const settingsContext = useSettingsContext()
  if (!settingsContext.settings) {
    return <>Please renavigate to this page</>;
  }
  else if (settingsContext.settings.reservation.allocationCheckerPublic) {
    return <>{props.children}</>;
  }
  else {
    return <RequireAuth onlyAdmin={true}>{props.children}</RequireAuth>
  }
}

function App() {
  const cookiesAccepted= getCookie("cookiesAccepted");
  const [isDialogCookieOpen, setDialogCookieOpen] = useState<boolean>(cookiesAccepted ? false : true);

  const onCookieDialogClose = () => {
    setCookie("cookiesAccepted", "true", 365);
    setDialogCookieOpen(false);
  }

  return (
    // <div className="flex items-center justify-center min-h-screen bg-slate-50">
    //   <div className="text-center">
    //     <h1 className="text-4xl font-bold text-slate-800">Under Maintenance</h1>
    //     <p className="text-slate-600 mt-2">We'll be back soon!</p>
    //   </div>
    // </div>
    <div className="flex flex-col min-h-screen bg-slate-50">
      <HouseProvider>
      <AuthProvider>
      <SettingsProvider>
        <SnackbarProvider maxSnack={1}>
            <Header/>
            <main className="flex-grow">
            <Routes>
              <Route key="route1" path="/login" element={<PageLogin />} />
              <Route path={"/belegung"} element={<PageSelectLocation/>}/>
              <Route path={"belegung/:id"}
                element={
                  <ConditionalAuth>
                    <PageAllocationChecker/>
                  </ConditionalAuth>
                }/>
              <Route path={"/"} element={<PageSelectLocation/>}/>
              <Route key="route4"
                path="/benutzer/erstellen"
                element={
                  <RequireAuth onlyAdmin={true}>
                    <PageSetUser/>
                  </RequireAuth>
                }
              />
              <Route key="route4"
                path="/benutzer/verwalten"
                element={
                  <RequireAuth onlyAdmin={true}>
                    <PageEditUser/>
                  </RequireAuth>
                }
              />
              <Route key="route5"
                path="/profil"
                element={
                  <RequireAuth>
                    <PageUserProfile/>
                  </RequireAuth>
                }
              />
              <Route key="route6"
                path="/buchung"
                element={
                  <RequireAuth>
                    <PageBookingStepper/>
                  </RequireAuth>
                }
              />
              <Route key="route7"
              path="/buchung/status"
              element={
                <RequireAuth onlyAdmin={true}>
                  <PageMyBookings key="icoming" viewType="incoming"/>
                </RequireAuth>
              }
              />
              <Route key="route8"
                path="/buchung/bearbeiten"
                element={
                  <RequireAuth onlyAdmin={true}>
                    <PageEditBookings/>
                  </RequireAuth>
                }
              />
              <Route key="route9"
                path="/buchung/erstellen"
                element={
                  <RequireAuth onlyAdmin={true}>
                    <PageBookingStepper showUserSelection={true}/>
                  </RequireAuth>
                }
              />
              <Route key="route10"
                path="/meinebuchung"
                element={
                  <RequireAuth >
                    <PageMyBookings key="mine" viewType="mine"/>
                  </RequireAuth>
                }
              />
              <Route key="route11"
                path="/einstellungen"
                element={
                  <RequireAuth onlyAdmin={true}>
                    <PageSettings/>
                  </RequireAuth>
                }
              />
              <Route key="route12"
                path="/auslastung"
                element={
                  <RequireAuth onlyAdmin={true}>
                    <PageOccupancy/>
                  </RequireAuth>
                }
              />
              <Route key="route12" path="/benutzer/registrieren" element={<PageRegister/>} />
              <Route key="route13" path="/benutzer/passwordreset" element={<PagePasswordReset/>} />
              <Route key="route13" path="/impressum" element={<PageImpressum/>} />
              <Route key="route14" path="*" element={"Not found"} />
            </Routes>
            </main>
            <Footer />
        </SnackbarProvider>
      </SettingsProvider>
      </AuthProvider>
      </HouseProvider>
      <DialogCookieBanner open={isDialogCookieOpen} onClose={onCookieDialogClose}></DialogCookieBanner>
    </div>

  );
}

export default App;
